import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './YogaArchetypeQuiz.css';

const YogaArchetypeQuiz = () => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState({
    archetype: '',
    description: '',
    emoji: '',
    yogaStyles: []
  });

  // Define archetypes with descriptions and emojis
  const archetypes = {
    explorer: {
      name: t("yoga_archetype_quiz.archetypes.explorer.name", "Explorer"),
      description: t("yoga_archetype_quiz.archetypes.explorer.description", "You seek variety and adventure in your practice. Always curious, you love discovering new ways to move your body and challenge yourself."),
      emoji: '🧭',
      styles: [t("yoga_styles.vinyasa.name", "Vinyasa"), t("yoga_styles.power.name", "Power")]
    },
    grounder: {
      name: t("yoga_archetype_quiz.archetypes.grounder.name", "Grounder"),
      description: t("yoga_archetype_quiz.archetypes.grounder.description", "You value stability and consistency. Your practice is about building a strong foundation and finding peace through steady, mindful movement."),
      emoji: '🌱',
      styles: [t("yoga_styles.hatha.name", "Hatha"), t("yoga_styles.restorative.name", "Restorative")]
    },
    firestarter: {
      name: t("yoga_archetype_quiz.archetypes.firestarter.name", "Firestarter"),
      description: t("yoga_archetype_quiz.archetypes.firestarter.description", "Passionate and energetic, you thrive on intensity. You push boundaries and use your practice to build strength and release powerful energy."),
      emoji: '🔥',
      styles: [t("yoga_styles.power.name", "Power"), t("yoga_styles.ashtanga.name", "Ashtanga")]
    },
    zenSeeker: {
      name: t("yoga_archetype_quiz.archetypes.zen_seeker.name", "Zen-Seeker"),
      description: t("yoga_archetype_quiz.archetypes.zen_seeker.description", "You practice yoga primarily for inner peace. Meditation and mindfulness are as important to you as the physical postures."),
      emoji: '☯️',
      styles: [t("yoga_styles.yin.name", "Yin"), t("yoga_styles.restorative.name", "Restorative")]
    }
  };

  // Define quiz questions
  const questions = [
    {
      question: t("yoga_archetype_quiz.questions.q1.question", "When you wake up in the morning, you typically feel:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q1.a1", "Ready to take on challenges and try something new"), archetype: "explorer", yogaStyles: ["Vinyasa", "Power"] },
        { text: t("yoga_archetype_quiz.questions.q1.a2", "In need of a consistent, grounding routine"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q1.a3", "Energetic and ready to move intensely"), archetype: "firestarter", yogaStyles: ["Ashtanga", "Power"] },
        { text: t("yoga_archetype_quiz.questions.q1.a4", "Contemplative, seeking inner calm"), archetype: "zenSeeker", yogaStyles: ["Yin"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q2.question", "Your ideal yoga class would:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q2.a1", "Incorporate varied sequences and creative movements"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q2.a2", "Focus on proper alignment and fundamentals"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q2.a3", "Be challenging and make you sweat"), archetype: "firestarter", yogaStyles: ["Power", "Ashtanga"] },
        { text: t("yoga_archetype_quiz.questions.q2.a4", "Include long-held poses and meditation"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q3.question", "When faced with stress, you typically:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q3.a1", "Try different approaches until you find what works"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q3.a2", "Return to trusted routines that calm you"), archetype: "grounder", yogaStyles: ["Hatha", "Restorative"] },
        { text: t("yoga_archetype_quiz.questions.q3.a3", "Channel it into physical activity"), archetype: "firestarter", yogaStyles: ["Power"] },
        { text: t("yoga_archetype_quiz.questions.q3.a4", "Sit with your emotions and process them mindfully"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q4.question", "Your friends would describe you as:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q4.a1", "Adventurous and spontaneous"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q4.a2", "Reliable and steady"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q4.a3", "Passionate and driven"), archetype: "firestarter", yogaStyles: ["Ashtanga"] },
        { text: t("yoga_archetype_quiz.questions.q4.a4", "Thoughtful and calm"), archetype: "zenSeeker", yogaStyles: ["Yin"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q5.question", "Your approach to learning is best described as:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q5.a1", "Experimenting with different methods"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q5.a2", "Building systematically on fundamentals"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q5.a3", "Diving in fully and intensely"), archetype: "firestarter", yogaStyles: ["Ashtanga", "Power"] },
        { text: t("yoga_archetype_quiz.questions.q5.a4", "Reflective and patient"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q6.question", "What draws you to yoga?"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q6.a1", "The variety of practices and continuous discovery"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q6.a2", "The structure and feeling of stability it provides"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q6.a3", "The physical challenge and strength building"), archetype: "firestarter", yogaStyles: ["Power", "Ashtanga"] },
        { text: t("yoga_archetype_quiz.questions.q6.a4", "The mindfulness and inner peace"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q7.question", "Your ideal vacation would be:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q7.a1", "Exploring new places and trying local activities"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q7.a2", "Returning to a favorite destination with a comfortable routine"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q7.a3", "An active adventure trip with challenging activities"), archetype: "firestarter", yogaStyles: ["Ashtanga", "Power"] },
        { text: t("yoga_archetype_quiz.questions.q7.a4", "A retreat focused on relaxation and self-reflection"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q8.question", "In a group setting, you typically:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q8.a1", "Bring fresh ideas and adaptability"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q8.a2", "Provide consistency and reliability"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q8.a3", "Energize others and push the group forward"), archetype: "firestarter", yogaStyles: ["Power"] },
        { text: t("yoga_archetype_quiz.questions.q8.a4", "Create harmony and thoughtful spaces"), archetype: "zenSeeker", yogaStyles: ["Yin"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q9.question", "Your living space is most likely:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q9.a1", "Eclectic with items from various travels and interests"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q9.a2", "Organized and functional with everything in its place"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q9.a3", "Vibrant and energetic with bold colors or statement pieces"), archetype: "firestarter", yogaStyles: ["Power"] },
        { text: t("yoga_archetype_quiz.questions.q9.a4", "Minimalist and calming with thoughtful touches"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q10.question", "When it comes to physical exercise, you prefer:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q10.a1", "Trying different activities and sports"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q10.a2", "Consistent routines that build steadily"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q10.a3", "High-intensity workouts that push your limits"), archetype: "firestarter", yogaStyles: ["Power", "Ashtanga"] },
        { text: t("yoga_archetype_quiz.questions.q10.a4", "Gentle movement focused on mind-body connection"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q11.question", "Your approach to breathing during yoga would be:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q11.a1", "Dynamic and flowing, changing with each movement"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q11.a2", "Steady and rhythmic, maintaining a consistent pattern"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q11.a3", "Powerful and energizing, fueling your practice"), archetype: "firestarter", yogaStyles: ["Ashtanga"] },
        { text: t("yoga_archetype_quiz.questions.q11.a4", "Deep and mindful, as a meditation in itself"), archetype: "zenSeeker", yogaStyles: ["Yin"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q12.question", "Your ideal yoga instructor would:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q12.a1", "Introduce new variations and creative sequences"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q12.a2", "Focus on proper form and progressive building blocks"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q12.a3", "Push you beyond your comfort zone"), archetype: "firestarter", yogaStyles: ["Power", "Ashtanga"] },
        { text: t("yoga_archetype_quiz.questions.q12.a4", "Create space for inner exploration and awareness"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q13.question", "After a yoga session, you feel best when:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q13.a1", "You've discovered new possibilities in your practice"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q13.a2", "You've improved your foundation and alignment"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q13.a3", "You've pushed through barriers and feel accomplished"), archetype: "firestarter", yogaStyles: ["Ashtanga", "Power"] },
        { text: t("yoga_archetype_quiz.questions.q13.a4", "You've achieved a state of deep relaxation and peace"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q14.question", "When learning a new yoga pose, you tend to:"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q14.a1", "Try multiple approaches and variations"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q14.a2", "Break it down methodically and build step by step"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q14.a3", "Challenge yourself to master it quickly through determination"), archetype: "firestarter", yogaStyles: ["Ashtanga", "Power"] },
        { text: t("yoga_archetype_quiz.questions.q14.a4", "Focus on how the pose feels internally rather than its appearance"), archetype: "zenSeeker", yogaStyles: ["Yin"] }
      ]
    },
    {
      question: t("yoga_archetype_quiz.questions.q15.question", "Which quality do you most value in your yoga practice?"),
      options: [
        { text: t("yoga_archetype_quiz.questions.q15.a1", "Creativity and exploration"), archetype: "explorer", yogaStyles: ["Vinyasa"] },
        { text: t("yoga_archetype_quiz.questions.q15.a2", "Stability and consistency"), archetype: "grounder", yogaStyles: ["Hatha"] },
        { text: t("yoga_archetype_quiz.questions.q15.a3", "Intensity and transformation"), archetype: "firestarter", yogaStyles: ["Power", "Ashtanga"] },
        { text: t("yoga_archetype_quiz.questions.q15.a4", "Mindfulness and inner peace"), archetype: "zenSeeker", yogaStyles: ["Yin", "Restorative"] }
      ]
    }
  ];

  // Handle radio input changes
  const handleAnswerChange = (questionIndex, answerIndex) => {
    setAnswers({
      ...answers,
      [questionIndex]: answerIndex
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Count archetype points
    const archetypePoints = {
      explorer: 0,
      grounder: 0,
      firestarter: 0,
      zenSeeker: 0
    };
    
    // Count yoga style points
    const yogaStylePoints = {
      Vinyasa: 0,
      Hatha: 0,
      Yin: 0,
      Restorative: 0,
      Power: 0,
      Ashtanga: 0
    };
    
    // Calculate points based on answers
    Object.keys(answers).forEach(questionIndex => {
      const question = questions[questionIndex];
      const answerIndex = answers[questionIndex];
      const selectedOption = question.options[answerIndex];
      
      // Add points to archetype
      archetypePoints[selectedOption.archetype]++;
      
      // Add points to yoga styles
      selectedOption.yogaStyles.forEach(style => {
        yogaStylePoints[style]++;
      });
    });
    
    // Determine top archetype
    let topArchetype = 'explorer';
    Object.keys(archetypePoints).forEach(archetype => {
      if (archetypePoints[archetype] > archetypePoints[topArchetype]) {
        topArchetype = archetype;
      }
    });
    
    // Determine top yoga styles (up to 2)
    const sortedStyles = Object.keys(yogaStylePoints)
      .sort((a, b) => yogaStylePoints[b] - yogaStylePoints[a])
      .slice(0, 2)
      .filter(style => yogaStylePoints[style] > 0);
    
    // Set result
    setResult({
      archetype: archetypes[topArchetype].name,
      description: archetypes[topArchetype].description,
      emoji: archetypes[topArchetype].emoji,
      yogaStyles: sortedStyles
    });
    
    // Show results
    setShowResults(true);
  };

  // Navigate to next question
  const handleNext = () => {
    setCurrentQuestion(currentQuestion + 1);
  };

  // Navigate to previous question
  const handlePrev = () => {
    setCurrentQuestion(currentQuestion - 1);
  };

  // Reset the quiz
  const handleReset = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setShowResults(false);
  };

  return (
    <div className="yoga-quiz-container">
      <h2 className="quiz-title">{t("yoga_archetype_quiz.heading", "Discover Your Yoga Archetype")}</h2>
      
      {!showResults ? (
        <form onSubmit={handleSubmit} className="quiz-form">
          <div className="question-container">
            <h3 className="question-number">
              {t("yoga_archetype_quiz.question_number", "Question {{current}} of {{total}}", { current: currentQuestion + 1, total: questions.length })}
            </h3>
            <fieldset className="question-fieldset">
              <legend className="question-text">{questions[currentQuestion].question}</legend>
              <div className="options-container">
                {questions[currentQuestion].options.map((option, index) => (
                  <div className="option" key={index}>
                    <input
                      type="radio"
                      id={`q${currentQuestion}-a${index}`}
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={answers[currentQuestion] === index}
                      onChange={() => handleAnswerChange(currentQuestion, index)}
                      required
                    />
                    <label htmlFor={`q${currentQuestion}-a${index}`}>
                      {option.text}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          </div>
          
          <div className="navigation-buttons">
            {currentQuestion > 0 && (
              <button 
                type="button" 
                className="nav-button prev-button" 
                onClick={handlePrev}
              >
                {t("yoga_archetype_quiz.previous", "Previous")}
              </button>
            )}
            
            {currentQuestion < questions.length - 1 ? (
              <button 
                type="button" 
                className="nav-button next-button" 
                onClick={handleNext}
                disabled={answers[currentQuestion] === undefined}
              >
                {t("yoga_archetype_quiz.next", "Next")}
              </button>
            ) : (
              <button 
                type="submit" 
                className="submit-button"
                disabled={Object.keys(answers).length < questions.length}
              >
                {t("yoga_archetype_quiz.submit", "Submit")}
              </button>
            )}
          </div>
          
          <div className="progress-indicator">
            <div 
              className="progress-bar" 
              style={{ width: `${((Object.keys(answers).length) / questions.length) * 100}%` }}
            ></div>
          </div>
        </form>
      ) : (
        <div className="results-container">
          <div className="result-card">
            <div className="result-emoji">{result.emoji}</div>
            <h3 className="result-title">
              {t("yoga_archetype_quiz.result_title", "You are a {{archetype}}", { archetype: result.archetype })}
            </h3>
            <p className="result-description">{result.description}</p>
            <div className="recommended-styles">
              <h4>{t("yoga_archetype_quiz.recommended_styles", "Recommended Yoga Styles:")}</h4>
              <ul>
                {result.yogaStyles.map((style, index) => (
                  <li key={index}>{t(`yoga_styles.${style.toLowerCase()}.name`, style)}</li>
                ))}
              </ul>
            </div>
            <button className="reset-button" onClick={handleReset}>
              {t("yoga_archetype_quiz.take_again", "Take Quiz Again")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default YogaArchetypeQuiz; 